export interface SidebarMenuItem {
  /** Ruta del routerLink (respetar el prefijo tal cual: './...' o '/...'). */
  route: string;
  /** Clave de traducción de la etiqueta. */
  labelKey: string;
  /** Texto de respaldo si la traducción no está disponible. */
  fallbackLabel: string;
  /** Icono base FontAwesome, ej. 'fa fa-building'. Cada vista agrega su sufijo
   *  de clase ('sidebar-item-icon' en el menú, 'cmdk-result-icon' en el buscador). */
  icon: string;
  /** Gate de permisos del ítem (OR). Ausente/vacío = visible para todos. */
  permissions?: string[];
  /** Gate de permisos del ítem (AND): exige tenerlos todos. Combinable con `permissions`. */
  permissionsAll?: string[];
  /** Clave de traducción del sufijo, ej. 'planificacion' -> "Etiqueta - Planificación". */
  labelSuffixKey?: string;
  /** Texto de respaldo del sufijo. */
  fallbackLabelSuffix?: string;
  /** Palabras clave adicionales para el buscador (no se muestran en el menú). */
  keywords?: string[];
}

export interface SidebarMenuSection {
  /** Encabezado de la sección (<details><summary>). Undefined = ítems planos. */
  labelKey?: string;
  /** Texto de respaldo del encabezado. */
  fallbackLabel?: string;
  /** Si el <details> arranca abierto. */
  open?: boolean;
  items: SidebarMenuItem[];
}

export interface SidebarMenuModule {
  /** Identificador estable del módulo. */
  id: string;
  /** Clave de traducción del nombre del módulo (texto expandido, título/h5
   *  compacto y etiqueta de módulo en el buscador). */
  labelKey: string;
  fallbackLabel: string;
  /** Gate de permisos del módulo (OR). Ausente/vacío = visible para todos. */
  permissions?: string[];
  /** Sufijo para los IDs del acordeón Bootstrap: heading{X} / collapse{X}. */
  accordionId: string;
  /** Logo de la vista expandida. */
  expandedLogo: string;
  /** Logo de la vista compacta (algunos módulos usan una variante "-chico"). */
  compactLogo: string;
  /** Logo ancho sin texto (usa las clases *-logo-wide). */
  wide?: boolean;
  /** Muestra el <strong> con el nombre en la vista expandida. */
  showLabel?: boolean;
  /** Clase modificadora de color del encabezado, ej. 'sidebar-module-link--backoffice'. */
  linkModifierClass?: string;
  sections: SidebarMenuSection[];
}

export const SIDEBAR_MENU: SidebarMenuModule[] = [
  {
    id: 'backoffice',
    labelKey: 'backoffice',
    fallbackLabel: 'Backoffice',
    permissions: ['admin_abm'],
    accordionId: 'Back',
    expandedLogo: 'assets/svgs/backofficeLogoAlt.svg',
    compactLogo: 'assets/svgs/backofficeLogoAlt.svg',
    showLabel: true,
    linkModifierClass: 'sidebar-module-link--backoffice',
    sections: [
      {
        items: [
          { route: './backoffice/inquilinos', labelKey: 'minquilinos', fallbackLabel: 'Inquilinos', icon: 'fa fa-building' },
          { route: './backoffice/usuarios', labelKey: 'musuarios', fallbackLabel: 'Usuarios', icon: 'fa fa-users', keywords: ['abm', 'alta'] },
          { route: './backoffice/perfiles', labelKey: 'mperfiles', fallbackLabel: 'Perfiles', icon: 'fa fa-id-badge', keywords: ['abm', 'permisos', 'roles'] },
          { route: './backoffice/seg-tareas', labelKey: 'msegtareas', fallbackLabel: 'Tareas de seguridad', icon: 'fa fa-user-shield', permissions: ['admin_ver_seg_tareas'] },
        ],
      },
    ],
  },
  {
    id: 'general',
    labelKey: 'general',
    fallbackLabel: 'General',
    permissions: ['general_abm', 'mant_abm', 'mant_ver_estado_ot', 'mant_ver_trans_estado_ot', 'mant_ver_talleres', 'mant_abm_talleres', 'general_ver_programaciones'],
    accordionId: 'Gral',
    expandedLogo: 'assets/svgs/generalLogoAlt.svg',
    compactLogo: 'assets/svgs/generalLogoAlt.svg',
    showLabel: true,
    linkModifierClass: 'sidebar-module-link--general',
    sections: [
      {
        labelKey: 'maestrosgenerales',
        fallbackLabel: 'Maestros generales',
        open: true,
        items: [
          { route: './general/equipo', labelKey: 'mequipo', fallbackLabel: 'Equipo', icon: 'fa fa-cogs', permissions: ['general_abm'] },
          { route: './mantenimiento/plan-mantenimiento', labelKey: 'planmantenimiento', fallbackLabel: 'Plan de mantenimiento', icon: 'fa fa-calendar-check', permissions: ['mant_abm'] },
          { route: './mantenimiento/articulo-previsto', labelKey: 'marticuloprevisto', fallbackLabel: 'Articulo previsto', icon: 'fa fa-boxes', permissions: ['mant_abm'] },
          { route: './mantenimiento/rrhh-previsto', labelKey: 'mrrhhprevisto', fallbackLabel: 'RRHH previsto', icon: 'fa fa-user-clock', permissions: ['mant_abm'] },
          { route: './mantenimiento/tarea-prevista', labelKey: 'mtareaprevista', fallbackLabel: 'Tarea prevista', icon: 'fa fa-tasks', permissions: ['mant_abm'] },
          { route: './mantenimiento/tarea', labelKey: 'mtarea', fallbackLabel: 'Tarea', icon: 'fa fa-clipboard-list', permissions: ['mant_abm'] },
          { route: './mantenimiento/tipo-mantenimiento', labelKey: 'mtipomantenimiento', fallbackLabel: 'Tipo de mantenimiento', icon: 'fa fa-wrench', permissions: ['mant_abm'] },
          { route: './mantenimiento/tipo-medicion', labelKey: 'mtipomedicion', fallbackLabel: 'Tipo de medicion', icon: 'fa fa-ruler-combined', permissions: ['mant_abm'] },
          { route: './general/tipo-empleado', labelKey: 'mtipoempleado', fallbackLabel: 'Tipo de empleado', icon: 'fa fa-user-cog', permissions: ['general_abm'] },
          { route: './general/ubicacion', labelKey: 'mubicacion', fallbackLabel: 'Ubicacion', icon: 'fa fa-map-marker-alt', permissions: ['general_abm'] },
          { route: './general/socio-negocio-ubicacion', labelKey: 'msocionegocioubicacion', fallbackLabel: 'Ubicacion de socio de negocio', icon: 'fa fa-map-marked-alt', permissions: ['general_abm'] },
          { route: './general/criticidad', labelKey: 'mcriticidad', fallbackLabel: 'Criticidad', icon: 'fa fa-exclamation-triangle', permissions: ['general_abm'] },
          { route: './general/tipo-equipo', labelKey: 'mtipoequipo', fallbackLabel: 'Tipo de equipo', icon: 'fa fa-layer-group', permissions: ['general_abm'] },
          { route: './general/estado-equipo', labelKey: 'mestadoequipo', fallbackLabel: 'Estado de equipo', icon: 'fa fa-toggle-on', permissions: ['general_abm'] },
          { route: './mantenimiento/prioridad', labelKey: 'mprioridad', fallbackLabel: 'Prioridad', icon: 'fa fa-flag', permissions: ['mant_abm'] },
          { route: './mantenimiento/estado-ot', labelKey: 'mestadoot', fallbackLabel: 'Estado OT', icon: 'fa fa-traffic-light', permissions: ['mant_ver_estado_ot'], keywords: ['ot'] },
          { route: './mantenimiento/transicion-estado-ot', labelKey: 'mtranestadosot', fallbackLabel: 'Transicion de estados OT', icon: 'fa fa-random', permissions: ['mant_ver_trans_estado_ot'], keywords: ['ot'] },
          { route: './mantenimiento/talleres', labelKey: 'mtalleres', fallbackLabel: 'Talleres', icon: 'fa fa-industry', permissions: ['mant_ver_talleres', 'mant_abm_talleres'] },
          { route: './general/tabla-generica', labelKey: 'mtablas', fallbackLabel: 'Tablas', icon: 'fa fa-table', permissions: ['general_ver_tablas'] },
          { route: './general/sync/datos-fuente', labelKey: 'importardatosmaestros', fallbackLabel: 'Importar datos maestros', icon: 'fa fa-file-import', permissions: ['general_import_data'] },
          { route: './general/parametros-generales', labelKey: 'mparametrosgenerales', fallbackLabel: 'Parametros generales', icon: 'fa fa-sliders-h', permissions: ['general_ver_parametros'] },
          { route: './general/programaciones-procesos', labelKey: 'mprogramacionesprocesos', fallbackLabel: 'Programaciones de ejecucion automatica', icon: 'fa fa-clock', permissions: ['general_ver_programaciones'] },
        ],
      },
      {
        labelKey: 'sincronizadossapb1',
        fallbackLabel: 'Sincronizados SAP B1',
        items: [
          { route: './general/articulo', labelKey: 'marticulo', fallbackLabel: 'Articulo', icon: 'fa fa-box', permissions: ['general_abm'], keywords: ['sap'] },
          { route: './general/socio-negocio', labelKey: 'msocionegocio', fallbackLabel: 'Socio de negocio', icon: 'fa fa-handshake', permissions: ['general_abm'], keywords: ['sap'] },
          { route: './general/tipo-socio-negocio', labelKey: 'mtiposocionegocio', fallbackLabel: 'Tipo socio de negocio', icon: 'fa fa-tags', permissions: ['general_abm'], keywords: ['sap'] },
          { route: './general/empleado', labelKey: 'mempleado', fallbackLabel: 'Empleado', icon: 'fa fa-user-tie', permissions: ['general_abm'], keywords: ['sap'] },
          { route: './general/sync', labelKey: 'msyncprocesos', fallbackLabel: 'Procesos de sincronizacion', icon: 'fa fa-sync-alt', permissions: ['general_sync_ver_logs'] },
        ],
      },
    ],
  },
  {
    id: 'parte',
    labelKey: 'partediario',
    fallbackLabel: 'Parte Diario',
    permissions: ['parte_abm', 'parte_ver', 'parte_crear'],
    accordionId: 'Parte',
    expandedLogo: 'assets/images/Logo-PARTOne-fondo-claro.png',
    compactLogo: 'assets/images/Logo-PARTOne-fondo-claro-chico.png',
    wide: true,
    sections: [
      {
        items: [
          { route: './parte/dash', labelKey: 'tablerocontrol', fallbackLabel: 'Tablero de control', icon: 'fa fa-chart-line' },
          { route: './parte/tarea-parte', labelKey: 'mtarea', fallbackLabel: 'Tarea', icon: 'fa fa-clipboard-check', permissions: ['parte_abm'] },
          { route: './parte/campo-usuario', labelKey: 'mcampousuario', fallbackLabel: 'Campo de usuario', icon: 'fa fa-list-alt', permissions: ['parte_abm'] },
          { route: './parte/grupo-campo-usuario', labelKey: 'mgrupocampousuario', fallbackLabel: 'Grupo campo de usuario', icon: 'fa fa-object-group', permissions: ['parte_abm'] },
          { route: './parte/modelo', labelKey: 'modelosparte', fallbackLabel: 'Modelos de parte', icon: 'fa fa-project-diagram', permissions: ['parte_abm'] },
          { route: '/parte/parte', labelKey: 'partediario', fallbackLabel: 'Parte diario', icon: 'fa fa-calendar-day' },
        ],
      },
    ],
  },
  {
    id: 'control',
    labelKey: 'controldocumental',
    fallbackLabel: 'Control Documental',
    permissions: ['docu_abm', 'docu_ver', 'docu_crear'],
    accordionId: 'Control',
    expandedLogo: 'assets/images/Logo-DOCOne-fondo-claro.png',
    compactLogo: 'assets/images/Logo-DOCOne-fondo-claro-chico.png',
    wide: true,
    sections: [
      {
        items: [
          { route: './controldocumental/dash', labelKey: 'tablerocontrol', fallbackLabel: 'Tablero de control', icon: 'fa fa-chart-pie' },
          { route: './controldocumental/tipo-documentacion', labelKey: 'mtipodocumentacion', fallbackLabel: 'Tipo de documentacion', icon: 'fa fa-sitemap', permissions: ['docu_abm'] },
          { route: './controldocumental/recurso-documentacion', labelKey: 'mrecursodocumentacion', fallbackLabel: 'Recurso documentacion', icon: 'fa fa-file-alt', permissions: ['docu_abm'] },
          { route: './controldocumental/socio-negocio-documentacion', labelKey: 'msocionegociodocumentacion', fallbackLabel: 'Socio negocio documentacion', icon: 'fa fa-file-signature', permissions: ['docu_abm'] },
          { route: './controldocumental/documentacion', labelKey: 'mdocumentacion', fallbackLabel: 'Documentacion', icon: 'fa fa-folder-open' },
        ],
      },
    ],
  },
  {
    id: 'check',
    labelKey: 'checklist',
    fallbackLabel: 'CheckList',
    permissions: ['check_abm', 'check_ver', 'check_crear'],
    accordionId: 'Check',
    expandedLogo: 'assets/images/Logo-CHECKOne-fondo-claro.png',
    compactLogo: 'assets/images/Logo-CHECKOne-fondo-claro-chico.png',
    wide: true,
    sections: [
      {
        items: [
          { route: './check/dash', labelKey: 'tablerocontrol', fallbackLabel: 'Tablero de control', icon: 'fa fa-chart-bar' },
          { route: './check/componente-check', labelKey: 'mcomponentecheck', fallbackLabel: 'Componente check', icon: 'fa fa-puzzle-piece', permissions: ['check_abm'] },
          { route: './check/modelo', labelKey: 'mmodelochecklist', fallbackLabel: 'Modelo checklist', icon: 'fa fa-clipboard-list', permissions: ['check_abm'] },
          { route: './check/check', labelKey: 'checklist', fallbackLabel: 'Checklist', icon: 'fa fa-check-double' },
        ],
      },
    ],
  },
  {
    id: 'operacion',
    labelKey: 'operacion',
    fallbackLabel: 'Operación',
    permissions: ['mant_ver_sol', 'mant_crear_sol', 'mant_ver_ot', 'mant_crear_ot', 'mant_vista_plan', 'mant_vista_solic', 'mant_vista_seguim', 'mant_ver_estado', 'mant_ver_medicion', 'mant_crear_medicion'],
    accordionId: 'Mant',
    expandedLogo: 'assets/svgs/eamoneLogoChico.svg',
    compactLogo: 'assets/svgs/eamoneLogoChico.svg',
    showLabel: true,
    linkModifierClass: 'sidebar-module-link--eamone',
    sections: [
      {
        items: [
          { route: './mantenimiento/dash', labelKey: 'tablerocontrol', fallbackLabel: 'Tablero de control', icon: 'fa fa-tachometer-alt' },
          { route: './mantenimiento/solicitud-mantenimiento/vista/seguimiento', labelKey: 'seguimientosolicitudesmantenimiento', fallbackLabel: 'Seguimiento solicitudes de mantenimiento', icon: 'fa fa-route', permissions: ['mant_vista_seguim'], keywords: ['sm'] },
          { route: './mantenimiento/solicitud-mantenimiento/vista/planificador', labelKey: 'solicitudesmantenimiento', fallbackLabel: 'Solicitudes mantenimiento', labelSuffixKey: 'planificacion', fallbackLabelSuffix: 'Planificacion', icon: 'fa fa-calendar-alt', permissions: ['mant_vista_plan'], keywords: ['sm'] },
          { route: './mantenimiento/solicitud-mantenimiento/vista/solicitante', labelKey: 'solicitudesmantenimiento', fallbackLabel: 'Solicitudes mantenimiento', labelSuffixKey: 'solicitante', fallbackLabelSuffix: 'Solicitante', icon: 'fa fa-user-edit', permissions: ['mant_vista_solic'], keywords: ['sm'] },
          { route: './mantenimiento/orden-trabajo/vista/planificador', labelKey: 'ordenestrabajo', fallbackLabel: 'Ordenes de trabajo', labelSuffixKey: 'planificacion', fallbackLabelSuffix: 'Planificacion', icon: 'fa fa-clipboard-list', permissions: ['mant_vista_plan'], keywords: ['ot'] },
          { route: './mantenimiento/orden-trabajo/vista/solicitante', labelKey: 'ordenestrabajo', fallbackLabel: 'Ordenes de trabajo', labelSuffixKey: 'solicitante', fallbackLabelSuffix: 'Solicitante', icon: 'fa fa-clipboard-check', permissionsAll: ['mant_vista_solic'], keywords: ['ot'] },
          { route: './mantenimiento/orden-trabajo/vista/solicitante-solicitudes', labelKey: 'ordenestrabajo', fallbackLabel: 'Ordenes de trabajo', labelSuffixKey: 'solicitud', fallbackLabelSuffix: 'Solicitud', icon: 'fa fa-clipboard', permissions: ['mant_crear_sol', 'mant_ver_ot'], keywords: ['ot'] },
          { route: './mantenimiento/medicion', labelKey: 'mediciones', fallbackLabel: 'Mediciones', icon: 'fa fa-ruler', permissions: ['mant_ver_medicion'] },
          { route: './mantenimiento/estado-equipo', labelKey: 'gestionmantenimientos', fallbackLabel: 'Gestion mantenimientos', icon: 'fa fa-tools', permissions: ['mant_crear_medicion'] },
        ],
      },
    ],
  },
  {
    id: 'reportes',
    labelKey: 'reportes',
    fallbackLabel: 'Reportes',
    permissions: ['mant_rep_costo_mant', 'mant_rep_hist_ubic', 'mant_rep_hist_estado_equipo', 'mant_rep_equipos', 'mant_rep_disp_eq', 'mant_rep_ots', 'mant_rep_tareas_pendiente'],
    accordionId: 'Reportes',
    expandedLogo: 'assets/svgs/reportesLogo.svg',
    compactLogo: 'assets/svgs/reportesLogo.svg',
    showLabel: true,
    linkModifierClass: 'sidebar-module-link--reportes',
    sections: [
      {
        items: [
          { route: './mantenimiento/reportes/costomantenimiento', labelKey: 'reportecostomantenimiento', fallbackLabel: 'Reporte costo mantenimiento', icon: 'fa fa-dollar-sign', permissions: ['mant_rep_costo_mant'] },
          { route: './mantenimiento/reportes/reporte-ot', labelKey: 'reporteordentrabajo', fallbackLabel: 'Reporte orden trabajo', icon: 'fa fa-file-invoice', permissions: ['mant_rep_ots'], keywords: ['ot'] },
          { route: './mantenimiento/reportes/informe-equipos', labelKey: 'reporteequipos', fallbackLabel: 'Reporte equipos', icon: 'fa fa-file-alt', permissions: ['mant_rep_equipos'] },
          { route: './mantenimiento/reportes/historial-estado-equipo', labelKey: 'reportehistorialestadoequipo', fallbackLabel: 'Reporte historial estado equipo', icon: 'fa fa-history', permissions: ['mant_rep_hist_estado_equipo'] },
          { route: './mantenimiento/reportes/historialubicacion', labelKey: 'reportehistorialubicacion', fallbackLabel: 'Reporte historial ubicacion', icon: 'fa fa-map', permissions: ['mant_rep_hist_ubic'] },
          { route: './mantenimiento/reportes/disponibilidad-equipos', labelKey: 'reportedisponibilidadequipos', fallbackLabel: 'Reporte disponibilidad equipos', icon: 'fa fa-chart-area', permissions: ['mant_rep_disp_eq'] },
          { route: './mantenimiento/reportes/reporte-ot/pendiente', labelKey: 'reportetareaspendientes', fallbackLabel: 'Reporte tareas pendientes', icon: 'fa fa-hourglass-half', permissions: ['mant_rep_tareas_pendiente'], keywords: ['ot'] },
        ],
      },
    ],
  },
];
